import React, { useState } from "react";
import { Button, Form, Input, Modal, notification } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import ForgotPasswordModal1 from "./forgot-password-modal-1";
import ForgotPasswordModal2 from "./forgot-password-modal-2";
import { authenticatedUserApi } from "api/authenticated-user-api";
import {
  createForgotPasswordToken,
  resetForgottenPassword,
} from "api/unauthenticated-user-api";
import logo from "./logo.png";

import "./index.scss";

// Three login modals
// The Forgot Password flow proceeds form LOGIN to FORGOT_PASSWORD_1 to
// FORGOT_PASSWORD_2.
// Two-factor authentication could be implemented by adding another modal.
const LOGIN = "LOGIN";
const FORGOT_PASSWORD_1 = "FORGOT_PASSWORD_1";
const FORGOT_PASSWORD_2 = "FORGOT_PASSWORD_2";

export const LoginModal = () => {
  const [modal, changeModal] = useState(LOGIN);
  const [email, setEmail] = useState("");

  /**
   * Handles form submission from ForgotPasswordModal1.
   * The server will send an email to the user with a one-time token for
   * resetting their password.
   * The view is switched to ForgotPasswordModal2 so the user can then enter
   * the one-time token and their new password.
   *
   * @param {object} formData - should include a validated email field
   * @returns void
   */
  function forgotPassword1Callback(formData) {
    // Send PUT to users/forgot_password
    createForgotPasswordToken(formData.email)
      // Switch to the second Forgot Password modal
      .then(() => {
        setEmail(formData.email);
        changeModal(FORGOT_PASSWORD_2);
      });
  }

  /**
   * Handles form submission from ForgotPasswordModal2.
   * A request to reset the user's password is sent to the server. A message
   * is displayed that either "Password Successfully Reset" or
   * "Unable to Reset Your Password" (most likely because the token
   * has expired).
   * The view is switched to LoginModal so the user can either login or
   * request a new Forgot Password token.
   *
   * @param {object} formData - should include a validated email field
   * @returns void
   */
  function forgotPassword2Callback(formData) {
    // Send POST to users/forgot_password to reset the password
    resetForgottenPassword(formData)
      // Switch to the second Forgot Password modal
      .then((response) => {
        if (response.ok) {
          notification.success({
            'message': 'Password Successfully Reset, redirecting to login page',
          });
          window.setTimeout(() =>{
            changeModal(LOGIN);
          }, 2000);
        } else {
          notification.error({
            'message': 'Unable to Reset Password',
          });
        }
      });
  }

  // Show one of the login modals
  switch (modal) {
    // First Forgot Password modal; has email input
    case FORGOT_PASSWORD_1:
      return (
        <ForgotPasswordModal1
          callback={forgotPassword1Callback}
          openLogin={() => changeModal(LOGIN)}
        />
      );

    // Second Forgot Password modal; has token, password, and confirmPassword inputs
    case FORGOT_PASSWORD_2:
      return (
        <ForgotPasswordModal2
          callback={forgotPassword2Callback}
          email={email}
          openLogin={() => changeModal(LOGIN)}
        />
      );

    // The primary login modal
    default:
      return (
        <Modal
          title={
            <>
              <img
                src={logo}
                style={{
                  width: "150px",
                  margin: "10px 0px",
                }}
              />
              <h2>DLA Knowledge Management</h2>
            </>
          }
          className="login-modal"
          visible={true}
          closable={false}
          footer={<div>Powered by Savannah</div>}
        >
          <Form
            initialValues={{
              remember: true,
            }}
            onFinish={authenticatedUserApi.loginEmailPassword}
            key="login-form"
          >
            <Form.Item
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please input your Email!",
                },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email" />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please input your Password!",
                },
              ]}
            >
              <Input
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                placeholder="Password"
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Log In
              </Button>
              <Button
                onClick={() => changeModal(FORGOT_PASSWORD_1)}
                type="link"
              >
                Forgot Password?
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      );
  }
};
